/**
 * @file src/components/PolicyAcceptanceCheckbox/PolicyAcceptanceCheckbox.tsx
 * @exports PolicyAcceptanceCheckbox
 * @summary Checkbox that asks the user to accept Terms and Conditions and Privacy Policy.
 *   Overrides Cerebellum's :focus-within focus-ring behaviour so the ring only appears
 *   when the checkbox input itself has keyboard focus, not when the label's links are focused.
 */
import { CheckboxItem, colors } from "@cerebruminc/cerebellum";
import React from "react";
import { Controller } from "react-hook-form";
import { styled } from "styled-components";
import { TOS_NODE_TYPE } from "src/const";
import { env } from "src/environment";

const ParagraphWrapper = styled.p`
	font-size: 15px;
	letter-spacing: 0.025em;
	line-height: 20px;
`;

const LinkWrapper = styled.a<{ $highContrast: boolean }>`
	color: ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.BLUE_100)};
	text-decoration-line: underline;
	font-style: italic;
`;
const CheckboxWrapper = styled.div`
	margin: 8px 0 22px;

	/* Override Cerebellum's :focus-within on the label, which shows the checkbox
	   focus ring when any descendant is focused — including links in the label.
	   Gated behind @supports selector(:has(*)) so browsers that lack :has()
	   fall back to Cerebellum's original :focus-within behaviour rather than
	   permanently hiding the focus ring (a11y regression). */
	@supports selector(:has(*)) {
		label:focus-within [data-testid="checkbox-visual"]::after {
			opacity: 0;
		}
		label:has(input:focus-visible) [data-testid="checkbox-visual"]::after {
			opacity: 1;
		}
	}
`;

function PolicyAcceptanceText({ isHighContrast }: { isHighContrast: boolean }) {
	return (
		<ParagraphWrapper data-sentry-unmask>
			I accept and agree to the{" "}
			<LinkWrapper
				href={env.TERMS_AND_CONDITIONS_URL}
				rel="noopener noreferrer"
				target="_blank"
				$highContrast={isHighContrast}
			>
				Terms and Conditions
			</LinkWrapper>{" "}
			and{" "}
			<LinkWrapper
				href={env.PRIVACY_POLICY_URL}
				rel="noopener noreferrer"
				target="_blank"
				$highContrast={isHighContrast}
			>
				Privacy Policy
			</LinkWrapper>
			.<br />
		</ParagraphWrapper>
	);
}

// TODO: replace any with a real type. PolicyAcceptanceCheckboxProps doesn't match the type of the control prop.
interface PolicyAcceptanceCheckboxProps {
	control: any;
	isHighContrast: boolean;
}
export function PolicyAcceptanceCheckbox({ control, isHighContrast }: PolicyAcceptanceCheckboxProps) {
	return (
		<Controller
			name={TOS_NODE_TYPE}
			control={control}
			render={({ field, formState }) => {
				const isSubmitted = formState.isSubmitted;
				const isChecked = Boolean(field.value);
				const hasError = Boolean(isSubmitted && !isChecked);
				function getInactiveColor(hasError: boolean) {
					if (isHighContrast) {
						return `${hasError ? colors.RED_130 : colors.BLACK}`;
					}
					return `${hasError ? colors.RED_100 : colors.COOL_GREY_50}`;
				}
				const inactiveColor = getInactiveColor(hasError);
				return (
					<CheckboxWrapper>
						<CheckboxItem
							active={isChecked}
							label={<PolicyAcceptanceText isHighContrast={isHighContrast} />}
							onClick={() => {
								field.onChange(!isChecked);
							}}
							value="Policy Acceptance Checkbox"
							labelColor={colors.RED_10}
							inactiveColor={inactiveColor}
						/>
					</CheckboxWrapper>
				);
			}}
		/>
	);
}
